<script setup lang="ts">
/**
 * /pay/[slug] — Page de paiement personnalisée pour une organisation.
 *
 * Le corps (orchestration Stripe/PayPal, SSR, surcharges visuelles, template)
 * vit dans le composant partagé `DonateCheckoutApp` — réutilisé à l'identique
 * par la route embarquée `/embed/[slug]` (widget cross-site). Cette page n'est
 * plus qu'un point d'entrée qui résout le slug et rend le composant en
 * `mode="pay"` (comportement historique inchangé).
 *
 * SEO + Open Graph + JSON-LD sont gérés par `usePaySlugSeo` (appelé DANS le
 * composant), qui n'émet le canonical/OG qu'en mode `pay`.
 */
import { useRoute } from "vue-router";

definePageMeta({
  layout: "payment",
});

const route = useRoute();
const slug = ((route.params as Record<string, string>).slug ?? "") as string;

// En mode aperçu (iframe du dashboard, `?preview=true`), on masque la scrollbar
// du document : elle est interne à l'iframe (cross-origin) donc le dashboard ne
// peut pas la cacher lui-même. Gaté STRICTEMENT sur preview → les vrais
// donateurs gardent une scrollbar normale. Karim 2026-08-15.
const isPreview = computed(() => route.query.preview === "true");
useHead(
  computed(() =>
    isPreview.value
      ? {
          style: [
            {
              innerHTML:
                "html,body{scrollbar-width:none!important;-ms-overflow-style:none!important;}*::-webkit-scrollbar{display:none!important;width:0!important;height:0!important;}",
            },
          ],
        }
      : {},
  ),
);
</script>

<template>
  <DonateCheckoutApp :slug="slug" mode="pay" />
</template>
